import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Table, { Column } from '../components/Table';
import ConfirmDialog from '../components/ConfirmDialog';
import { accessRightService } from '../services/adminApi';

interface AccessRight {
  id: string;
  code: string;
  description?: string;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_view: boolean;
}

export default function UserAccessRights() {
  const [rights, setRights] = useState<AccessRight[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccessRight | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await accessRightService.list();
      setRights(res.data.accessRights || []);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await accessRightService.remove(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete access right');
    }
  };

  const columns: Column<AccessRight>[] = [
    { key: 'code', header: 'Code', sortable: true },
    { key: 'description', header: 'Description', sortable: true },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-3">
          <Link to={`/admin/access-rights/${row.id}`} className="text-blue-600 hover:underline text-sm">
            Edit / Permissions
          </Link>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline text-sm">
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Access Rights</h1>
        <Link to="/admin/access-rights/new">
          <Button>Add Access Right</Button>
        </Link>
      </div>

      <Table
        columns={columns}
        rows={rights}
        loading={loading}
        emptyMessage="No access rights yet"
        searchable
        searchPlaceholder="Search by code or description..."
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Access Right"
        message={`Delete access right "${deleteTarget?.code}"? Users assigned this right will lose those permissions.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
